import { Button, Carousel, Skeleton, Space } from 'antd';
import React, { Suspense, useCallback, useRef, useState } from 'react';
import { useFragment, useLazyLoadQuery } from 'react-relay';

import { capitalizeFirstLetter } from '../../functions';
import { CommentsListFragment$key } from '../../graphql/__generated__/CommentsListFragment.graphql';
import { CommentsQuery as CommentsQueryType } from '../../graphql/__generated__/CommentsQuery.graphql';
import { CommentsQuery } from '../../graphql/CommentsQuery';
import { CommentsListFragment } from '../../graphql/fragments/CommentsListFragment';
import { CommentType } from '../../types';
import ActivityLogsFeed from '../ActivityLogsFeed/ActivityLogsFeed';
import CommentsForm from './CommetsForm';

type PageActivityType = 'all' | 'comments' | 'history';

interface IActivityComponent {
    model_id: string;
    model_type: 'ScheduleItem' | 'Task';
    isDragging?: boolean;
    isAccess: { create?: boolean };
}

const ActivityComponent: React.FC<IActivityComponent> = ({ model_id, model_type, isAccess, isDragging }) => {
    const [fetchKey, setFetchKey] = useState(0);
    const [activeTab, setActiveTab] = useState<PageActivityType>('all');
    const carouselRef = useRef<any>();

    const queryData = useLazyLoadQuery<CommentsQueryType>(
        CommentsQuery,
        {
            commentable_id: model_id,
            order_by: ['create_at', 'DESC'],
        },
        { fetchPolicy: 'network-only' },
    );
    const fragmentData = useFragment<CommentsListFragment$key>(CommentsListFragment, queryData);
    const connectionItemsId = (fragmentData.comments as any)?.__id;
    const comments = fragmentData.comments?.edges?.map((edge) => edge?.node) as any as CommentType[];

    const handleAddComment = () => {
        setActiveTab('comments');
        carouselRef.current.goTo(1);
    };

    const refreshData = useCallback(() => {
        setFetchKey((prevKey) => prevKey + 1);
    }, []);

    const renderButton = (subActiveTab: PageActivityType) => {
        return (
            <Button
                key={subActiveTab}
                type={activeTab === subActiveTab ? 'primary' : 'default'}
                onClick={() => {
                    setActiveTab(subActiveTab);
                    carouselRef.current.goTo(subActiveTab === 'comments' ? 1 : 0);
                }}
            >
                {capitalizeFirstLetter(subActiveTab)}
            </Button>
        );
    };

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                }}
            >
                {activeTab === 'all' && isAccess.create ? (
                    <Button onClick={handleAddComment}>Add Comment</Button>
                ) : (
                    <div></div>
                )}
                <Space>{[renderButton('all'), renderButton('history'), renderButton('comments')]}</Space>
            </div>
            <Carousel ref={carouselRef} dots={false} lazyLoad={'progressive'} effect="fade">
                <div>
                    <ActivityLogsFeed model_id={model_id} fetchKey={fetchKey} isDragging={isDragging} />
                </div>
                <div>
                    <CommentsForm
                        comments={comments}
                        connectionId={connectionItemsId}
                        model_id={model_id}
                        model_type={model_type}
                        refreshData={refreshData}
                        isDragging={isDragging}
                        isAccess={isAccess}
                    />
                </div>
            </Carousel>
        </div>
    );
};

export default function Activity(props: IActivityComponent) {
    return (
        <Suspense fallback={<Skeleton active />}>
            <ActivityComponent {...props} />
        </Suspense>
    );
}
